import { usePlans } from "@/lib/plans";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ArrowUp, Check, ChevronDown, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { inr, readPricing } from "@/lib/pricing";

/**
 * Premium plan display shared by the landing page and the in-app plan picker.
 * All numbers come from the same server pricing the checkout uses (plan.pricing) and the admin-managed
 * plan/service matrix, so nothing here can drift from what the customer is charged.
 *
 * Layout: phones   -> vertical stack of premium cards (portrait and landscape) + sticky plan switcher chips, CTA inside every card
 *         tablets  -> 2 columns
 *         desktops -> 4 columns, recommended plan lifted
 *         below    -> comparison table (services x plans) that only scrolls sideways on narrow screens
 */
export function usePlanMatrix() {
  const [rows, setRows] = useState([]);
  const { plans: livePlans } = usePlans();   // re-read the matrix whenever the live plans refresh (admin edit, tab focus)
  useEffect(() => {
    let alive = true, timer = null, tries = 0;
    const go = () => api.get("/plans/service-matrix", { params: { _: Date.now() }, timeout: 30000 })
      .then(({ data }) => { if (alive && Array.isArray(data)) setRows(data); })
      .catch(() => { if (alive && tries++ < 4) timer = setTimeout(go, 2000 * tries); });   // the cards already show their own feature lines meanwhile
    go();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [livePlans]);
  return useMemo(() => Object.fromEntries(rows.map((r) => [r.id, (r.services || []).filter((s) => s.enabled && Number(s.quantity) > 0)])), [rows]);
}

const dark = "navi-pc--dark";

/* ---------- feature comparison helpers ----------
   A plan's feature list is compared with the plan just below it (by price).
   "new"  = the feature does not exist in the lower plan                (e.g. D, E)
   "up"   = same feature, bigger amount ("4 washes" -> "8 washes")
   "same" = already included in the lower plan                           (e.g. A, B, C)                       */
const NUM = /\d[\d,.]*/g;
const norm = (t) => String(t).toLowerCase().replace(/\s+/g, " ").trim();
const keyOf = (t) => norm(String(t).replace(NUM, "#"));
const valOf = (t) => { const m = String(t).match(/\d[\d,.]*/); return m ? m[0] : null; };
const numOf = (t) => { const v = valOf(t); return v == null ? null : parseFloat(v.replace(/,/g, "")); };
const labelOf = (t) => {
  const stripped = String(t).replace(NUM, "").replace(/\s{2,}/g, " ").replace(/^[\s\-\u2013\u00b7:/]+|[\s\-\u2013\u00b7:/]+$/g, "");
  return stripped.length >= 4 ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : String(t);
};
/** The plan's advantages: its own highlights; if the admin wrote none, the services it includes. */
function splitPlanFeature(text) {
  const value = String(text);
  const split = value.lastIndexOf(" · ");
  if (split < 0) return { label: value, quantity: "" };
  const label = value.slice(0, split).trim();
  const quantity = value.slice(split + 3).trim();
  return label && /\d/.test(quantity) ? { label, quantity } : { label: value, quantity: "" };
}

export function planTexts(plan, services = []) {
  const feats = (Array.isArray(plan?.features) ? plan.features : []).filter(Boolean).map(String);
  return feats.length ? feats : services.map((x) => `${x.display_name} \u00b7 ${x.quantity}`);
}
export function diffTexts(texts, prevTexts) {
  if (!prevTexts) return texts.map(() => "same");
  const exact = new Set(prevTexts.map(norm));
  const byKey = new Map(prevTexts.map((t) => [keyOf(t), t]));
  return texts.map((t) => {
    if (exact.has(norm(t))) return "same";
    const q = byKey.get(keyOf(t));
    if (q !== undefined) { const a = numOf(t), b = numOf(q); return a != null && b != null && a > b ? "up" : "same"; }
    return "new";
  });
}
const tierOf = (plans) => {
  const price = (p) => { try { return readPricing(p)?.selling; } catch { return undefined; } };
  const pos = new Map(plans.map((p, i) => [p.id, i]));
  return [...plans].sort((a, b) => (price(a) ?? Infinity) - (price(b) ?? Infinity) || pos.get(a.id) - pos.get(b.id));
};

/** Plan card: name, struck-through price, discounted price, advantages (what is NEW vs the plan below is highlighted). */
export function PlanCard({ plan, index = 0, services = [], prevPlan = null, prevServices = [], onChoose, ctaLabel = "Choose plan", compact = false, selected = false, tone = "dark", interval, onHover, onLeave, tier = 0, tierCount = 0 }) {
  const pr = readPricing(plan);
  const popular = !!plan.popular;
  // Pointer-following light: the card edge glows where the cursor is (CSS reads --mx / --my).
  const spot = (e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`); e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`); };
  const texts = planTexts(plan, services);
  const marks = diffTexts(texts, prevPlan ? planTexts(prevPlan, prevServices) : null);
  const all = texts.map((text, i) => ({ key: `f${i}`, text, status: marks[i], k: i }));
  const newCount = all.filter((x) => x.status === "new").length;
  const upCount = all.filter((x) => x.status === "up").length;
  const shown = all;   // every specification is always visible - no "show more / show less"
  return (
    <article
      data-testid={`plan-card-${plan.id}`}
      onMouseEnter={onHover ? (e) => onHover(plan, e.currentTarget) : undefined}
      onMouseLeave={onLeave}
      onPointerMove={spot}
      className={`navi-pc ${tone === "dark" ? dark : ""} ${popular ? "navi-pc--popular" : ""} ${selected ? "navi-pc--selected" : ""} ${compact ? "navi-pc--compact" : ""}`}
    >
      {popular && <div className="navi-pc-ribbon">Most popular</div>}
      {tierCount > 1 && (() => {
        const drops = Math.min(tierCount, 6), lit = Math.min(tier, drops - 1);
        return (
          <div className="navi-pc-meter" role="img" aria-label={`Care level ${tier + 1} of ${tierCount}`}>
            {Array.from({ length: drops }, (_, d) => (
              <svg key={d} viewBox="0 0 24 24" className={d <= lit ? "is-on" : ""} aria-hidden="true"><path d="M12 2.6c3.4 4.4 6.6 7.6 6.6 11.6a6.6 6.6 0 0 1-13.2 0c0-4 3.2-7.2 6.6-11.6z" /></svg>
            ))}
            <span>Level {tier + 1}</span>
          </div>
        );
      })()}
      <h3 className="text-lg font-semibold tracking-tight">{plan.name}</h3>

      {pr ? (
        <div className="mt-4" data-testid={`plan-price-${plan.id}`}>
          <div className="flex min-h-[1.5rem] flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
            {pr.hasDiscount ? (<>
              <span className="line-through decoration-2 decoration-red-400/80 navi-pc-mute font-medium" data-testid="plan-mrp">{inr(pr.mrp)}</span>
              <span className="navi-pc-off">{Math.round(pr.pct)}% off</span>
            </>) : null}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-[2.5rem] leading-none font-bold tracking-tight" data-testid="plan-selling-price">{inr(pr.selling)}</span>
            <span className="text-sm navi-pc-mute">/ {interval || plan.interval || "month"}</span>
          </div>
          {pr.hasDiscount && <div className="navi-pc-save">You save {inr(pr.discount)}</div>}
        </div>
      ) : <div className="mt-4 text-sm navi-pc-mute">Price unavailable</div>}

      {prevPlan && (newCount + upCount) > 0 && (
        <p className="navi-pc-delta" data-testid={`plan-delta-${plan.id}`}>Everything in <b>{prevPlan.name}</b>, plus <em>{newCount + upCount} extra</em></p>
      )}
      <ul className="mt-5 space-y-2.5" data-testid={`plan-perks-${plan.id}`}>
        {shown.map((x, n) => {
          const feature = splitPlanFeature(x.text);
          return (
            <li key={x.key} style={{ "--k": n }} className={`navi-pc-li is-${x.status} flex gap-2 text-[0.875rem] leading-snug navi-pc-soft`}>
              <span className={`navi-pc-mark is-${x.status}`} aria-hidden="true">
                {x.status === "new" ? <Plus /> : x.status === "up" ? <ArrowUp /> : <Check />}
              </span>
              <span className="navi-pc-li-text">
                <span className="navi-pc-feature-name">{feature.label}</span>
                {feature.quantity && <span className="navi-pc-quantity">{feature.quantity}</span>}
              </span>
              {x.status === "new" && <em className="navi-pc-tag">New</em>}
              {x.status === "up" && <em className="navi-pc-tag is-up">More</em>}
            </li>
          );
        })}
      </ul>

      <div className="navi-pc-cta mt-6 grid grid-cols-1 gap-2">
        <button type="button" data-testid={`plan-cta-${plan.id}`} onClick={() => onChoose?.(plan)} className={`navi-pc-btn ${popular ? "is-primary" : ""}`}><span>{ctaLabel}</span><ArrowRight className="navi-pc-arrow h-4 w-4" aria-hidden="true" /></button>
      </div>
    </article>
  );
}

/** Every feature of every plan in one table. NEW / MORE cells show what each tier adds over the one below it. */
function PlanCompare({ tiers, matrix }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => {
    const out = [], idx = new Map();
    tiers.forEach((p, i) => {
      const texts = planTexts(p, matrix[p.id] || []);
      const marks = diffTexts(texts, i ? planTexts(tiers[i - 1], matrix[tiers[i - 1].id] || []) : null);
      texts.forEach((t, n) => {
        const k = keyOf(t);
        let r = idx.get(k);
        if (!r) { r = { key: k, label: labelOf(t), cells: {} }; idx.set(k, r); out.push(r); }
        r.cells[p.id] = { val: valOf(t), status: marks[n] };
      });
    });
    return out;
  }, [tiers, matrix]);
  if (tiers.length < 2 || !rows.length) return null;
  return (
    <div className="navi-pc-compare-wrap">
      <button type="button" className="navi-pc-compare-btn" aria-expanded={open} data-testid="plan-compare-toggle" onClick={() => setOpen((v) => !v)}>
        Compare all plans <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div className="navi-pc-compare" role="region" aria-label="Plan comparison" tabIndex={0} data-testid="plan-compare">
          <table>
            <thead><tr>
              <th scope="col"><span className="sr-only">Feature</span></th>
              {tiers.map((p) => { const pr = readPricing(p); return (
                <th scope="col" key={p.id} className={p.popular ? "is-pop" : ""}>{p.name}{pr ? <small>{inr(pr.selling)}</small> : null}</th>
              ); })}
            </tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={r.key} style={{ "--r": ri }}>
                  <th scope="row">{r.label}</th>
                  {tiers.map((p) => {
                    const c = r.cells[p.id];
                    if (!c) return <td key={p.id} className="is-no" aria-label="Not included">{"\u2014"}</td>;
                    return (
                      <td key={p.id} className={`is-${c.status}${p.popular ? " is-pop" : ""}`}>
                        {c.val ? <b>{c.val}</b> : <Check className="h-4 w-4" aria-label="Included" />}
                        {c.status === "new" && <i className="navi-pc-newdot">New</i>}
                        {c.status === "up" && <i className="navi-pc-newdot is-up">More</i>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Phones show the cards side by side in a swipe row; every other screen stacks / grids them. Ask the browser which one is live.
const isRowOf = (r) => !!r && r.scrollWidth > r.clientWidth + 8 && getComputedStyle(r).overflowX !== "visible";
const centerSlotIn = (r, slot, behavior = "auto") => { if (r && slot) r.scrollTo({ left: slot.offsetLeft - (r.clientWidth - slot.offsetWidth) / 2, behavior }); };

export default function PlanShowcase({ plans = [], planOptions = () => [], onChoose, onDetails, onHover, onLeave, tone = "dark", ctaLabel, compact = false, selectedId, columnsClass, compare = false }) {
  const matrix = usePlanMatrix();
  const rail = useRef(null);
  const [active, setActive] = useState(plans.find((p) => p.popular)?.id || plans[0]?.id);
  useEffect(() => { if (!active && plans[0]) setActive(plans.find((p) => p.popular)?.id || plans[0].id); }, [plans, active]);
  // "previous plan" = the next cheaper one, whatever order the cards are shown in
  const tiers = useMemo(() => tierOf(plans), [plans]);
  const prevOf = useMemo(() => Object.fromEntries(tiers.map((p, i) => [p.id, i ? tiers[i - 1] : null])), [tiers]);
  const tierIdx = useMemo(() => Object.fromEntries(tiers.map((p, i) => [p.id, i])), [tiers]);

  const jump = (id) => {
    setActive(id);
    const root = rail.current;
    const el = root?.querySelector(`[data-testid="plan-card-${id}"]`);
    if (!el) return;
    if (isRowOf(root)) centerSlotIn(root, el.closest("[data-planid]") || el, "smooth");
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // keep the chip row in sync while the customer scrolls (down the stack) or swipes (along the row)
  useEffect(() => {
    const root = rail.current;
    if (!root) return undefined;
    let io = null, raf = 0, wasHidden = true;
    const onSwipe = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const mid = root.scrollLeft + root.clientWidth / 2;
        let best = null, gap = Infinity;
        root.querySelectorAll("[data-planid]").forEach((n) => { const d = Math.abs(n.offsetLeft + n.offsetWidth / 2 - mid); if (d < gap) { gap = d; best = n; } });
        if (best) setActive(best.dataset.planid);
      });
    };
    const setup = () => {
      if (io) { io.disconnect(); io = null; }
      root.removeEventListener("scroll", onSwipe);
      if (!root.clientWidth) { wasHidden = true; return; }     // section hidden (home-service focus): wait until it is shown again
      if (isRowOf(root)) {
        root.addEventListener("scroll", onSwipe, { passive: true });
        if (wasHidden) centerSlotIn(root, root.querySelector(".navi-pc--popular")?.closest("[data-planid]") || root.querySelector("[data-planid]"));   // open on the recommended plan
      } else if (typeof IntersectionObserver !== "undefined") {
        io = new IntersectionObserver((entries) => { entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.dataset.planid); }); }, { root: null, rootMargin: "-35% 0px -45% 0px", threshold: [0, 0.01] });
        root.querySelectorAll("[data-planid]").forEach((n) => io.observe(n));
      }
      wasHidden = false;
    };
    setup();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(setup) : null;
    ro?.observe(root);
    return () => { ro?.disconnect(); if (io) io.disconnect(); root.removeEventListener("scroll", onSwipe); if (raf) cancelAnimationFrame(raf); };
  }, [plans.length]);

  return (
    <div data-testid="plan-showcase">
      {/* phone plan switcher */}
      <div className="navi-pc-chips sm:hidden" role="tablist" aria-label="Plans">
        {plans.map((p) => <button key={p.id} role="tab" aria-selected={active === p.id} type="button" onClick={() => jump(p.id)} className={`navi-pc-chip ${active === p.id ? "is-on" : ""} ${tone === "dark" ? "navi-pc--dark" : ""}`}>{p.name}</button>)}
      </div>
      <div ref={rail} className={`navi-pc-rail ${columnsClass || "sm:grid-cols-2 xl:grid-cols-4"}`}>
        {plans.map((p, i) => {
          const prev = prevOf[p.id];
          return (
            <div key={p.id} data-planid={p.id} className="navi-pc-slot" style={{ "--i": i }}>
              <PlanCard plan={p} index={i} tone={tone} compact={compact} services={matrix[p.id] || []} prevPlan={prev} prevServices={prev ? matrix[prev.id] || [] : []} options={planOptions(p.id)}
                onChoose={onChoose} onDetails={onDetails} onHover={onHover} onLeave={onLeave} ctaLabel={ctaLabel} selected={selectedId === p.id} tier={tierIdx[p.id] ?? 0} tierCount={tiers.length} />
            </div>
          );
        })}
      </div>
      {compare && <PlanCompare tiers={tiers} matrix={matrix} />}
    </div>
  );
}
